import React from 'react';
import PropTypes from 'prop-types';

import {Box, Stack, Typography, Paper, Divider, Button, TextField, IconButton, SwitchCheckbox} from 'views/components';
import {Translate, translate, DatePickerYearMonth} from 'views/module';
import {DeleteForever, Add} from 'views/icons';

import moment from 'moment';

const WorkHistoryEdit = (props) => {
  const DEFAULT_WORK_ENTRY = {
    employerName: '',
    startDate: '',
    endDate: '',
    canContact: false,
    referenceContactInfo: '',
  };

  const {bioQuestions, setFields, setIsComplete} = props;

  const [workHistory, setWorkHistory] = React.useState([DEFAULT_WORK_ENTRY]);

  // Initialize from bioQuestions or fields
  React.useEffect(() => {
    const sourceHistory = bioQuestions?.workHistory || [];

    setWorkHistory(sourceHistory.length ? sourceHistory : [DEFAULT_WORK_ENTRY]);
  }, [bioQuestions]);

  // Sync with parent fields
  React.useEffect(() => {
    const filteredWorkHistory = workHistory.filter(
        (entry) => entry.employerName.trim() && entry.startDate,
    );
    setFields((prev) => ({...prev, workHistory: filteredWorkHistory}));
  }, [workHistory, setFields]);

  // Completion state
  React.useEffect(() => {
    setIsComplete(workHistory.length > 0);
  }, [workHistory]);

  const handleChange = (index, field) => (value) => {
    setWorkHistory((prev) => {
      return prev.map((entry, i) => {
        if (i !== index) return entry;

        let nextValue = value;

        if (field === 'startDate' || field === 'endDate') {
          nextValue = value ? moment(value).startOf('month').toISOString() : '';
        }

        return {
          ...entry,
          [field]: nextValue,
        };
      });
    });
  };

  const handleAdd = () => {
    setWorkHistory((prev) => [...prev, {...DEFAULT_WORK_ENTRY}]);
  };

  const handleDelete = (index) => {
    setWorkHistory((prev) => prev.filter((_, i) => i !== index));
  };

  const formatDateRange = (startDate, endDate) => {
    const t = translate('caregiver.CaregiverBiography.WorkHistory.Summary');
    const format = (d) => (d && moment(d).isValid() ? moment(d).format('MMM YYYY') : '');
    const start = format(startDate);
    const end = format(endDate);

    if (start && end) return `${start} - ${end}`;
    if (start) return `${start} - ${t?.Present || translate('caregiver.CaregiverBiography.WorkHistory.Summary.Present')}`;
    if (end) return (t?.UntilDate || translate('caregiver.CaregiverBiography.WorkHistory.Summary.UntilDate')).replace('#{date}', end);
    return t?.DatesNotSpecified || translate('caregiver.CaregiverBiography.WorkHistory.Summary.DatesNotSpecified');
  };

  return (
    <Stack spacing={3}>
      <Typography variant="h6" textAlign="center">
        <Translate expression="caregiver.CaregiverBiography.WorkHistory.Title" />
      </Typography>

      <Stack spacing={2.5}>
        {
          workHistory.map((entry, index) => (
            <Paper key={`work-${index}`} sx={{p: 2.5, borderRadius: 2}}>
              <Stack spacing={2}>
                <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                  <Stack spacing={0.25}>
                    <Typography variant="subtitle1" sx={{fontWeight: 600}}>
                      {entry.employerName?.trim() ||
                      translate('caregiver.CaregiverBiography.WorkHistory.EmploymentLabel', {number: index + 1})}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {formatDateRange(entry.startDate, entry.endDate)}
                    </Typography>
                  </Stack>
                  <IconButton color="error" onClick={() => handleDelete(index)}>
                    <DeleteForever fontSize="small" />
                  </IconButton>
                </Stack>

                <Divider />

                <TextField
                  label={translate('caregiver.CaregiverBiography.WorkHistory.Employer.Label')}
                  placeholder={translate('caregiver.CaregiverBiography.WorkHistory.Employer.Placeholder')}
                  value={entry.employerName}
                  onChange={handleChange(index, 'employerName')}
                  fullWidth
                />

                <Stack direction={{xs: 'column', sm: 'row'}} spacing={2}>
                  <DatePickerYearMonth
                    label={translate('caregiver.CaregiverBiography.WorkHistory.StartDate.Label')}
                    format="MMM YYYY"
                    value={entry.startDate ? moment(entry.startDate) : null}
                    onChange={handleChange(index, 'startDate')}
                    textFieldProps={{
                      fullWidth: true,
                      placeholder: translate('caregiver.CaregiverBiography.WorkHistory.StartDate.Placeholder'),
                    }}
                  />
                  <DatePickerYearMonth
                    label={translate('caregiver.CaregiverBiography.WorkHistory.EndDate.Label')}
                    format="MMM YYYY"
                    value={entry.endDate ? moment(entry.endDate) : null}
                    onChange={handleChange(index, 'endDate')}
                    textFieldProps={{
                      fullWidth: true,
                      placeholder: translate('caregiver.CaregiverBiography.WorkHistory.EndDate.Placeholder'),
                    }}
                  />
                </Stack>

                <SwitchCheckbox
                  value={Boolean(entry.canContact)}
                  onChange={(checked) => handleChange(index, 'canContact')(checked)}
                  label={translate('caregiver.CaregiverBiography.WorkHistory.CanContact.Label')}
                />

                {
                  entry.canContact && (
                    <TextField
                      label={translate('caregiver.CaregiverBiography.WorkHistory.ReferenceContactInfo.Label')}
                      placeholder={translate('caregiver.CaregiverBiography.WorkHistory.ReferenceContactInfo.Placeholder')}
                      value={entry.referenceContactInfo}
                      onChange={handleChange(index, 'referenceContactInfo')}
                      fullWidth
                      multiline
                      minRows={3}
                    />
                  )
                }
              </Stack>
            </Paper>
          ))
        }
      </Stack>

      <Button
        variant="outlined"
        color="primary"
        onClick={handleAdd}
        startIcon={<Add />}
        sx={{alignSelf: 'center', borderStyle: 'dashed', borderWidth: 1, px: 3, py: 1.5}}
      >
        <Translate expression="caregiver.CaregiverBiography.WorkHistory.AddEmployerButton" />
      </Button>

      {
        workHistory.length > 0 && (
          <Paper sx={{p: 2.5, borderRadius: 2}}>
            <Stack spacing={1.5}>
              <Typography variant="subtitle1" sx={{fontWeight: 600}}>
                <Translate
                  expression="caregiver.CaregiverBiography.WorkHistory.Summary.Title"
                  substitute={{
                    count: workHistory.length,
                    positions:
                    workHistory.length === 1 ?
                      translate('caregiver.CaregiverBiography.WorkHistory.Summary.PositionSingular') :
                      translate('caregiver.CaregiverBiography.WorkHistory.Summary.PositionPlural'),
                  }}
                />
              </Typography>
              <Box display="flex" flexDirection="column" gap={0.75}>
                {
                  workHistory.map((entry, index) => (
                    <Typography key={`summary-${index}`} variant="body2">
                      <Typography component="span" variant="body2" sx={{fontWeight: 600}}>
                        {entry.employerName ||
                      translate('caregiver.CaregiverBiography.WorkHistory.Summary.UnnamedEmployer')}
                      </Typography>
                      {` • ${formatDateRange(entry.startDate, entry.endDate)}`}
                      {entry.canContact && ` • ${translate('caregiver.CaregiverBiography.WorkHistory.Summary.ReferenceAvailable')}`}
                    </Typography>
                  ))
                }
              </Box>
            </Stack>
          </Paper>
        )
      }
    </Stack>
  );
};

WorkHistoryEdit.propTypes = {
  bioQuestions: PropTypes.object.isRequired,
  fields: PropTypes.object.isRequired,
  setFields: PropTypes.func.isRequired,
  setIsComplete: PropTypes.func,
};

export default React.memo(WorkHistoryEdit);
